import { onScopeDispose, watch } from 'vue'
import router from '@/router'
import { useMenuStore } from '@/stores/menu'
import { usePermStore } from '@/stores/perm'
import { clearAccessToken, tokenState } from '@/utils/token'
import { markLoggedOut } from '@/utils/pkce'

const API_BASE = (import.meta.env.VITE_API_BASE || '/api').replace(/\/+$/, '')
const RECONNECT_DELAYS = [1000, 5000, 30000]

/**
 * 订阅中心登出事件（SSE）。
 *
 * <p>用 fetch + ReadableStream 而不是 EventSource：EventSource 不能带 Authorization，
 * 把令牌放 query 会写进 access log。连接按令牌 sid 对应的中心 HttpSession 归属，
 * 另一站退出时本标签页能秒级回到登录页，而不是等下一次 401。
 *
 * <p>SSE 连接 30 分钟超时、网关也可能断流，所以断流后在"仍持有令牌"时按 1s/5s/30s 重连；
 * 令牌被清（主动退出/401）则不再重连。
 */
export function useSsoEvents(): void {
  let controller: AbortController | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let attempt = 0
  let stopped = false

  function clearReconnect(): void {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function disconnect(): void {
    clearReconnect()
    controller?.abort()
    controller = null
  }

  function scheduleReconnect(token: string): void {
    if (stopped || tokenState.accessToken.value !== token) {
      return
    }
    const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)]
    attempt += 1
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      if (!stopped && tokenState.accessToken.value === token) {
        void connect(token)
      }
    }, delay)
  }

  function onLogout(): void {
    disconnect()
    attempt = 0
    clearAccessToken()
    useMenuStore().reset()
    usePermStore().reset()
    markLoggedOut()
    void router.replace({ name: 'login' })
  }

  async function connect(token: string): Promise<void> {
    disconnect()
    controller = new AbortController()
    try {
      const response = await fetch(`${API_BASE}/auth/v1/sso/events`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      })
      if (!response.ok || response.body === null) {
        scheduleReconnect(token)
        return
      }
      attempt = 0
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      while (!stopped) {
        const { value, done } = await reader.read()
        if (done) {
          break
        }
        buffer += decoder.decode(value, { stream: true })
        let boundary = buffer.indexOf('\n\n')
        while (boundary >= 0) {
          const frame = buffer.slice(0, boundary)
          buffer = buffer.slice(boundary + 2)
          // Spring 的 SseEmitter 输出 `event:logout`（冒号后无空格），这里必须容忍两种写法
          if (/^event:\s*logout\s*$/m.test(frame)) {
            onLogout()
            return
          }
          boundary = buffer.indexOf('\n\n')
        }
      }
      scheduleReconnect(token)
    } catch {
      // 主动 disconnect（abort）时 token 已被清，scheduleReconnect 会自行短路；
      // 其它断流（网络/网关）按退避重连。
      scheduleReconnect(token)
    }
  }

  watch(tokenState.accessToken, (token) => {
    if (token) {
      attempt = 0
      void connect(token)
    } else {
      disconnect()
    }
  }, { immediate: true })

  onScopeDispose(() => {
    stopped = true
    disconnect()
  })
}
